/**
 * CinemaExitButton — the only affordance left on screen in cinema mode.
 *
 * Two competing needs: it must never be prominent enough to spoil a screen
 * capture, but a user who forgets the shortcut must never feel trapped in a
 * chromeless app. The resolution is time-based rather than opacity-only — a
 * "Press Esc to exit" hint sits next to the button for a few seconds on entry
 * and then fades away for good, so the escape route is obvious exactly when
 * it's needed and gone by the time anyone starts recording.
 *
 * MUST be rendered OUTSIDE whatever wrapper hides the rest of the chrome, or it
 * disappears along with everything else.
 */
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCinemaStore } from '@/stores/cinemaStore'

/** How long the "Press Esc" hint stays up after entering cinema mode. */
const HINT_MS = 3500

export function CinemaExitButton() {
  const active = useCinemaStore((s) => s.active)
  const exit = useCinemaStore((s) => s.exit)
  const [showHint, setShowHint] = useState(false)

  useEffect(() => {
    if (!active) {
      setShowHint(false)
      return
    }
    setShowHint(true)
    const t = window.setTimeout(() => setShowHint(false), HINT_MS)
    return () => window.clearTimeout(t)
  }, [active])

  if (!active) return null

  return (
    <div className="fixed right-4 top-4 z-[100] flex items-center gap-2">
      <span
        className={cn(
          'select-none rounded-md border border-white/10 bg-black/70 px-2 py-1',
          'text-[11px] font-medium text-white/70 backdrop-blur-sm',
          'transition-opacity duration-700',
          showHint ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      >
        Press <kbd className="font-semibold text-white">Esc</kbd> to exit cinema mode
      </span>

      <button
        type="button"
        onClick={exit}
        title="Exit cinema mode (Esc)"
        aria-label="Exit cinema mode"
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/15
                   bg-black/70 text-white/80 opacity-45 backdrop-blur-sm transition-opacity
                   duration-200 hover:opacity-100 focus-visible:opacity-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
